import { useEffect, useState } from "react";

import {
  type CockpitContext,
  type CockpitInvoice,
  type CockpitShipment,
  type EmailDetail,
  getCockpitContext,
} from "@/lib/emails";

interface ContextRailProps {
  email: EmailDetail | null;
  /** Optional pre-fetched context. When provided, the rail uses it
   *  directly without calling /api/cockpit/context. Tests + the gate
   *  smoke wire this so we can render without network. */
  context?: CockpitContext | null;
}

/** Right rail of the cockpit (brief §9). Phase 8 wires the shipment +
 *  invoice widgets to the new /api/cockpit/context endpoint (which
 *  itself fans out to the Oprentis + LogisBill adapters behind a
 *  feature flag). Until the real adapters ship, the response is
 *  ``source: "mock"`` and the rail labels each widget accordingly. */
export function ContextRail({ email, context }: ContextRailProps) {
  const [fetched, setFetched] = useState<CockpitContext | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!email || context !== undefined) {
      // Either nothing to fetch, or the parent already provided
      // pre-fetched context (e.g. tests, or a future SSR path).
      return;
    }
    let cancelled = false;
    setLoading(true);
    getCockpitContext(email.id)
      .then((data) => {
        if (!cancelled) setFetched(data);
      })
      .catch(() => {
        // The route already swallows adapter failures; a network or
        // 404 here leaves the widgets in their "no data" state.
        if (!cancelled) setFetched(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [email, context]);

  if (!email) {
    return (
      <aside
        data-testid="context-rail-empty"
        className="hidden w-72 shrink-0 border-l border-op-border bg-op-surface px-4 py-4 text-xs text-op-muted xl:block"
      >
        Select a case to see its context.
      </aside>
    );
  }

  const effective = context ?? fetched;
  const shipment = effective?.shipment ?? null;
  const invoice = effective?.invoice ?? null;
  const source = effective?.source ?? null;

  return (
    <aside
      data-testid="context-rail"
      data-source={source ?? "loading"}
      className="hidden w-72 shrink-0 flex-col gap-4 border-l border-op-border bg-op-surface px-4 py-4 xl:flex"
    >
      <EntityList email={email} />
      <ShipmentWidget shipment={shipment} loading={loading} source={source} />
      <InvoiceWidget invoice={invoice} loading={loading} source={source} />
    </aside>
  );
}

function EntityList({ email }: { email: EmailDetail }) {
  const rows: Array<{ key: string; value: string }> = [];
  const e = email.entities;
  if (e.lrn) rows.push({ key: "LRN", value: e.lrn });
  if (e.po) rows.push({ key: "PO", value: e.po });
  if (e.truck) rows.push({ key: "Truck", value: e.truck });
  if (e.client) rows.push({ key: "Client", value: e.client });
  if (e.amount) rows.push({ key: "Amount", value: e.amount });
  if (e.site) rows.push({ key: "Site", value: e.site });
  return (
    <section data-testid="context-rail-entities">
      <h3 className="mb-2 text-[10px] uppercase tracking-wider text-op-muted">
        Entities
      </h3>
      {rows.length === 0 ? (
        <p className="text-xs text-op-muted">No entities extracted.</p>
      ) : (
        <ul className="space-y-1.5">
          {rows.map((r) => (
            <li
              key={`${r.key}-${r.value}`}
              className="flex items-baseline justify-between gap-3"
            >
              <span className="font-mono text-[10px] uppercase tracking-wider text-op-muted">
                {r.key}
              </span>
              <span className="truncate font-mono text-xs text-op-primary" title={r.value}>
                {r.value}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function ShipmentWidget({
  shipment,
  loading,
  source,
}: {
  shipment: CockpitShipment | null;
  loading: boolean;
  source: "mock" | "real" | null;
}) {
  return (
    <section
      data-testid="context-rail-shipment"
      className="rounded border border-op-border bg-op-base px-3 py-3"
    >
      <header className="mb-2 flex items-center justify-between gap-2">
        <h3 className="text-[10px] uppercase tracking-wider text-op-muted">
          Shipment status
        </h3>
        {source === "mock" ? (
          <span
            data-testid="context-rail-shipment-mock-badge"
            className="rounded bg-op-accent-muted px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-op-accent"
            title="Mocked — Oprentis adapter wires in a follow-up"
          >
            mock
          </span>
        ) : null}
      </header>
      {loading && !shipment ? (
        <p className="text-xs text-op-muted">Loading…</p>
      ) : shipment ? (
        <div className="space-y-1 text-xs">
          <p className="text-op-primary">
            {shipment.origin} → {shipment.destination}
          </p>
          <p className="text-op-secondary">
            <span
              data-testid="context-rail-shipment-status"
              className="font-mono text-[10px] uppercase tracking-wider text-op-muted"
            >
              {shipment.status.replace(/_/g, " ")}
            </span>
            <span className="ml-2">{shipment.eta_relative}</span>
          </p>
          {shipment.truck ? (
            <p className="font-mono text-[11px] text-op-secondary">
              Truck <span className="text-op-primary">{shipment.truck}</span>
            </p>
          ) : null}
        </div>
      ) : (
        <p className="text-xs text-op-muted">No shipment data for this case.</p>
      )}
    </section>
  );
}

function InvoiceWidget({
  invoice,
  loading,
  source,
}: {
  invoice: CockpitInvoice | null;
  loading: boolean;
  source: "mock" | "real" | null;
}) {
  return (
    <section
      data-testid="context-rail-invoice"
      className="rounded border border-op-border bg-op-base px-3 py-3"
    >
      <header className="mb-2 flex items-center justify-between gap-2">
        <h3 className="text-[10px] uppercase tracking-wider text-op-muted">
          Invoice match
        </h3>
        {source === "mock" ? (
          <span
            data-testid="context-rail-invoice-mock-badge"
            className="rounded bg-op-accent-muted px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-op-accent"
            title="Mocked — LogisBill adapter wires in a follow-up"
          >
            mock
          </span>
        ) : null}
      </header>
      {loading && !invoice ? (
        <p className="text-xs text-op-muted">Loading…</p>
      ) : invoice ? (
        <div className="space-y-1 text-xs">
          <p className="text-op-primary">
            <span
              data-testid="context-rail-invoice-matched"
              className="font-mono"
            >
              {invoice.matched_items}
            </span>{" "}
            of{" "}
            <span className="font-mono">{invoice.total_items}</span> line items
            matched
          </p>
          <p className="text-op-secondary">
            Amount{" "}
            <span className="font-mono text-op-primary">{invoice.amount}</span>
          </p>
          {invoice.mismatch_reason ? (
            <p className="text-[11px] text-op-amber">
              {invoice.mismatch_reason}
            </p>
          ) : null}
        </div>
      ) : (
        <p className="text-xs text-op-muted">No invoice data for this case.</p>
      )}
    </section>
  );
}
